import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../../App'
import DetailsBox, { DetailRow } from '../../../../components/Cards/DetailsBox'
import SmallModal from '../../../../components/Modals/SmallModal'
import S3DocumentUploadDisplay from '../../../../components/images/S3DocumentUploadDisplay'
import ImagingReviewDrawer, { IMAGING_STATUS_COLORS } from './ImagingReviewDrawer'
import BigModal from '../../../../components/Modals/BigModal'

// Re-exported for existing importers (RadiationDosesTab)
export { IMAGING_STATUS_COLORS }

const BODY_REGION_OPTIONS = [
  'Head',
  'Cervical Spine',
  'Thoracic Spine',
  'Lumbar Spine',
  'Chest',
  'Abdomen',
  'Pelvis',
  'Shoulder',
  'Humerus',
  'Elbow',
  'Forearm',
  'Wrist',
  'Hand',
  'Hip',
  'Femur',
  'Knee',
  'Tibia/Fibula',
  'Ankle',
  'Foot',
]

const DOSE_UNITS = ['mGy·cm²', 'µGy·m²', 'mGy', 'mSv']

const PREGNANCY_OPTIONS = [
  'Not Applicable',
  'Not Pregnant',
  'Pregnant',
  'Unknown',
]

const userName = (user) =>
  user ? `${user.firstName} ${user.lastName}` : ''

const NewRequestDialog = ({ open, onClose, epcrID, modalities, onCreated }) => {
  const [modalityKey, setModalityKey] = useState('')
  const [bodyRegions, setBodyRegions] = useState([])
  const [laterality, setLaterality] = useState('Not Applicable')
  const [clinicalIndication, setClinicalIndication] = useState('')
  const [pregnancyStatus, setPregnancyStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const { setToast } = useContext(GlobalContext)

  const modality = modalities.find((option) => option.key === modalityKey)

  const handleSubmit = (e) => {
    e.preventDefault()
    setSaving(true)
    API.post(`/epcr/${epcrID}/imaging-requests`, {
      modalityKey,
      bodyRegions,
      laterality,
      clinicalIndication,
      pregnancyStatus: pregnancyStatus || undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Imaging request submitted' })
          setModalityKey('')
          setBodyRegions([])
          setLaterality('Not Applicable')
          setClinicalIndication('')
          setPregnancyStatus('')
          onCreated(res.data)
        } else {
          setToast({
            message:
              res.data?.errorMessage ?? 'Unable to submit the imaging request',
            severity: 'error',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <BigModal open={open} onClose={onClose}>
      <Stack gap={1} component="form" onSubmit={handleSubmit}>
        <Typography variant="title3">Request an Imaging Study</Typography>
        <FormControl required>
          <InputLabel>Imaging Modality</InputLabel>
          <Select
            value={modalityKey}
            onChange={(e) => setModalityKey(e.target.value)}
            label="Imaging Modality"
          >
            {modalities.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          freeSolo
          options={BODY_REGION_OPTIONS}
          value={bodyRegions}
          onChange={(e, value) => setBodyRegions(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Body Location"
              helperText="Press 'Enter' after typing a custom location"
            />
          )}
        />
        <FormControl>
          <InputLabel>Laterality</InputLabel>
          <Select
            value={laterality}
            onChange={(e) => setLaterality(e.target.value)}
            label="Laterality"
          >
            {['Not Applicable', 'Left', 'Right', 'Bilateral'].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          value={clinicalIndication}
          onChange={(e) => setClinicalIndication(e.target.value)}
          label="Clinical Indication"
          helperText="Why is this study needed?"
          multiline
          minRows={2}
          required
        />
        {modality?.ionising && (
          <FormControl required>
            <InputLabel>Pregnancy Status</InputLabel>
            <Select
              value={pregnancyStatus}
              onChange={(e) => setPregnancyStatus(e.target.value)}
              label="Pregnancy Status"
            >
              {PREGNANCY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {modality?.ionising && (
          <Alert severity="info">
            {modality.label} uses ionising radiation — a pregnancy status must
            be recorded before the study can be approved.
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={!modalityKey || !clinicalIndication || saving}
        >
          Submit Request
        </Button>
      </Stack>
    </BigModal>
  )
}

export const ImpressionDialog = ({
  request,
  epcrID,
  onClose,
  onUpdated,
  // Fired when scan images are attached/removed inside the dialog, so the
  // caller's list stays fresh even if the impression is never submitted
  onImagesChanged,
}) => {
  const { user } = useContext(UserContext)
  const [text, setText] = useState(request?.clinicalImpression?.text ?? '')
  const [doseValue, setDoseValue] = useState(request?.dose?.value ?? '')
  const [doseUnit, setDoseUnit] = useState(request?.dose?.unit ?? DOSE_UNITS[0])
  const [exposures, setExposures] = useState(request?.dose?.exposures ?? '')
  const [device, setDevice] = useState(request?.dose?.device ?? '')
  // Who operated the device - drives per-employee dose tracking. Defaults to
  // the logged-in clinician.
  const [performedBy, setPerformedBy] = useState(
    request?.performedBy ?? {
      _id: user._id,
      firstName: user.firstName,
      lastName: user.lastName,
    },
  )
  const [staffOptions, setStaffOptions] = useState([])
  const [saving, setSaving] = useState(false)
  const { setToast } = useContext(GlobalContext)

  useEffect(() => {
    if (!request) return
    API.post('/users-in-team', {}).then((res) => {
      if (res.status === 200) setStaffOptions(res.data)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!request])

  if (!request) return null

  const attachImage = (s3key) => {
    API.post(`/imaging-requests/${request._id}/images`, { s3key }).then(
      (res) => {
        if (res.status === 200) onImagesChanged?.(res.data)
        else {
          setToast({
            message: res.data?.errorMessage ?? 'Unable to attach the image',
            severity: 'error',
          })
        }
      },
    )
  }

  const removeImage = (newDocsArray, s3key) => {
    API.post(`/imaging-requests/${request._id}/remove-image`, { s3key }).then(
      (res) => {
        if (res.status === 200) onImagesChanged?.(res.data)
      },
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSaving(true)
    API.post(`/imaging-requests/${request._id}/impression`, {
      text,
      performedBy: performedBy?._id,
      dose:
        request.modality?.ionising && doseValue !== ''
          ? {
              value: Number(doseValue),
              unit: doseUnit,
              exposures: exposures === '' ? undefined : Number(exposures),
              device: device || undefined,
            }
          : undefined,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({ message: 'Clinical impression recorded' })
          onUpdated(res.data)
        } else {
          setToast({
            message:
              res.data?.errorMessage ?? 'Unable to record the impression',
            severity: 'error',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <BigModal open={!!request} onClose={onClose}>
      <Stack gap={1} component="form" onSubmit={handleSubmit}>
        <Typography variant="title3">
          Clinical Impression — {request.modality?.label}
        </Typography>
        <S3DocumentUploadDisplay
          title="Scan Images"
          caption="Upload a photo or export of the scan/X-ray alongside your impression"
          existingDocuments={(request.images || []).map(
            (image) => image.s3key,
          )}
          s3folder={`epcr/${epcrID ?? request.epcr}/imaging/${request._id}`}
          actionAfterSubmit={attachImage}
          actionAfterDelete={removeImage}
          showAsRows
        />
        <TextField
          value={text}
          onChange={(e) => setText(e.target.value)}
          label="Clinical Impression"
          helperText="Your interpretation of the images"
          multiline
          minRows={3}
          required
        />
        <Autocomplete
          options={staffOptions}
          value={performedBy}
          getOptionLabel={(option) =>
            `${option?.firstName ?? ''} ${option?.lastName ?? ''}`.trim()
          }
          isOptionEqualToValue={(a, b) => a?._id === b?._id}
          onChange={(e, value) => setPerformedBy(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Performed By"
              helperText={
                request.modality?.ionising
                  ? 'Who operated the device — used for their radiation dose record'
                  : 'Who performed the study'
              }
              required
            />
          )}
        />
        {request.modality?.ionising && (
          <>
            <Typography variant="title3">Radiation Dose</Typography>
            <Typography variant="caption">
              Copy the reading from the device's post-exposure display. Most
              X-ray units report a dose–area product (DAP) in mGy·cm² or
              µGy·m² — pick the unit shown on your device. Leave blank if the
              device gives no readout.
            </Typography>
            <Stack direction="row" gap={1}>
              <TextField
                value={doseValue}
                onChange={(e) => setDoseValue(e.target.value)}
                label="Dose"
                type="number"
                inputProps={{ min: 0, step: 'any' }}
                helperText="Total for all exposures in this study"
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={doseUnit}
                  onChange={(e) => setDoseUnit(e.target.value)}
                  label="Unit"
                >
                  {DOSE_UNITS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" gap={1}>
              <TextField
                value={exposures}
                onChange={(e) => setExposures(e.target.value)}
                label="Exposures"
                type="number"
                inputProps={{ min: 0 }}
                helperText="Number of exposures taken"
                fullWidth
              />
              <TextField
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                label="Device / Unit Used"
                helperText="e.g. the unit's asset number"
                fullWidth
              />
            </Stack>
          </>
        )}
        <Button
          type="submit"
          variant="contained"
          color="success"
          disabled={!text || !performedBy || saving}
        >
          Save & Complete Study
        </Button>
      </Stack>
    </BigModal>
  )
}

const RequestCard = ({
  request,
  epcrID,
  viewerIsApprover,
  onReview,
  onRecordImpression,
  onUpdated,
  onDeleted,
}) => {
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const isRequester =
    String(request.requestedBy?._id ?? request.requestedBy) ===
    String(user._id)
  const actionable = ['Approved', 'Completed'].includes(request.status)

  // Cancelling removes the request outright, so it disappears from the
  // approver worklist too
  const handleCancel = () => {
    API.delete(`/imaging-requests/${request._id}`).then((res) => {
      if (res.status === 200) {
        setToast({ message: 'Request cancelled' })
        onDeleted(request)
      } else {
        setToast({
          message: res.data?.errorMessage ?? 'Unable to cancel the request',
          severity: 'error',
        })
      }
    })
  }

  const attachImage = (s3key) => {
    API.post(`/imaging-requests/${request._id}/images`, { s3key }).then(
      (res) => {
        if (res.status === 200) onUpdated(res.data)
        else {
          setToast({
            message: res.data?.errorMessage ?? 'Unable to attach the image',
            severity: 'error',
          })
        }
      },
    )
  }

  const removeImage = (newDocsArray, s3key) => {
    API.post(`/imaging-requests/${request._id}/remove-image`, { s3key }).then(
      (res) => {
        if (res.status === 200) onUpdated(res.data)
      },
    )
  }

  return (
    <Stack component={Paper} variant="outlined" padding={1} gap={1} sx={{border: 'solid 1px #1E7F88'}}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="title3">{request.modality?.label}</Typography>
          <Chip
            label={request.status}
            size="small"
            color={IMAGING_STATUS_COLORS[request.status] ?? 'default'}
            sx={{ borderRadius: 1 }}
          />
        </Stack>
        <Typography variant="button">
          {moment(request.createdAt).format('DD/MM HH:mm')}
        </Typography>
      </Box>
      <DetailRow
        label="Body Location"
        value={[
          (request.bodyRegions || []).join(', '),
          request.laterality && request.laterality !== 'Not Applicable'
            ? request.laterality
            : '',
        ]
          .filter(Boolean)
          .join(' — ')}
      />
      <DetailRow
        label="Clinical Indication"
        value={request.clinicalIndication ?? ''}
      />
      {request.modality?.ionising && request.pregnancyStatus && (
        <DetailRow label="Pregnancy Status" value={request.pregnancyStatus} />
      )}
      <DetailRow label="Requested By" value={userName(request.requestedBy)} />
      {request.decidedBy && (
        <DetailRow
          label={`${request.status === 'Rejected' ? 'Rejected' : 'Approved'} By`}
          value={`${userName(request.decidedBy)} — ${moment(
            request.decidedAt,
          ).format('DD/MM HH:mm')}${
            request.decisionNotes ? ` — ${request.decisionNotes}` : ''
          }`}
        />
      )}
      {request.status === 'Cancelled' && (
        <DetailRow
          label="Cancelled By"
          value={`${userName(request.cancelledBy)} — ${moment(
            request.cancelledAt,
          ).format('DD/MM HH:mm')}`}
        />
      )}

      {(actionable || request.images?.length > 0) && (
        <S3DocumentUploadDisplay
          // Remount when images change elsewhere (e.g. uploaded from the
          // impression dialog) - the component only reads existingDocuments
          // on mount
          key={`images-${(request.images || []).length}`}
          title="Images"
          existingDocuments={(request.images || []).map(
            (image) => image.s3key,
          )}
          s3folder={`epcr/${epcrID}/imaging/${request._id}`}
          actionAfterSubmit={actionable ? attachImage : undefined}
          actionAfterDelete={actionable ? removeImage : undefined}
          viewOnly={!actionable}
          showAsRows
        />
      )}

      {request.performedBy && (
        <DetailRow label="Performed By" value={userName(request.performedBy)} />
      )}
      {request.clinicalImpression?.text && (
        <>
          <Divider />
          <DetailRow
            label="Clinical Impression"
            value={request.clinicalImpression.text}
          />
          <DetailRow
            label="Recorded By"
            value={`${userName(request.clinicalImpression.recordedBy)} — ${moment(
              request.clinicalImpression.recordedAt,
            ).format('DD/MM HH:mm')}`}
          />
        </>
      )}
      {request.dose?.value != null && (
        <DetailRow
          label="Radiation Dose"
          value={`${request.dose.value} ${request.dose.unit ?? ''}${
            request.dose.exposures
              ? ` — ${request.dose.exposures} exposure${
                  request.dose.exposures === 1 ? '' : 's'
                }`
              : ''
          }${request.dose.device ? ` — ${request.dose.device}` : ''}`}
        />
      )}

      {(request.status === 'Requested' || actionable) && (
        <Stack direction="row" gap={1} justifyContent="flex-end">
          {(isRequester || viewerIsApprover) &&
            ['Requested', 'Approved'].includes(request.status) && (
              <Button size="small" color="error" onClick={handleCancel}>
                Cancel Request
              </Button>
            )}
          {request.status === 'Requested' && viewerIsApprover && (
            <Button
              size="small"
              variant="contained"
              color="anMainBlue"
              onClick={() => onReview(request)}
            >
              Review
            </Button>
          )}
          {actionable && (
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() => onRecordImpression(request)}
            >
              {request.clinicalImpression?.text
                ? 'Edit Impression'
                : 'Record Impression'}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  )
}

// Lists a record's imaging requests, with role-appropriate actions. Used in
// the ePCR editor (allowNewRequests) and the read-only record view (title +
// hideWhenEmpty).
const ImagingRequestsPanel = ({
  epcrID,
  allowNewRequests,
  title,
  hideWhenEmpty,
  // Editor hooks - keep the assess.modules summary entry in step with the
  // request (see imagingAssessEntry.js)
  onRequestCreated,
  onRequestUpdated,
  onRequestDeleted,
}) => {
  const [requests, setRequests] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [viewerIsApprover, setViewerIsApprover] = useState(false)
  const [openNew, setOpenNew] = useState(false)
  const [reviewing, setReviewing] = useState(null)
  const [recordingImpression, setRecordingImpression] = useState(null)
  const settings = useContext(TenantSettingsContext).orgConfig?.orgSettings
  const imagingSettings = settings?.patientRecordSettings?.imagingSettings

  useEffect(() => {
    if (!epcrID) return
    API.get(`/epcr/${epcrID}/imaging-requests`).then((res) => {
      if (res.status === 200) {
        setRequests(res.data.requests ?? [])
        setViewerIsApprover(res.data.viewerIsApprover ?? false)
      }
      setLoaded(true)
    })
  }, [epcrID])

  const replaceRequest = (updated) => {
    setRequests((prev) =>
      prev.map((request) =>
        request._id === updated._id ? updated : request,
      ),
    )
    onRequestUpdated?.(updated)
  }

  const removeRequest = (removed) => {
    setRequests((prev) =>
      prev.filter((request) => request._id !== removed._id),
    )
    onRequestDeleted?.(removed)
  }

  if (!loaded || (hideWhenEmpty && requests.length === 0)) return null

  const content = (
    <Stack gap={1}>
      {allowNewRequests && (
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="anMainBlue"
            size="small"
            onClick={() => setOpenNew(true)}
          >
            New Imaging Request
          </Button>
        </Box>
      )}
      {requests.length === 0 && (
        <Alert severity="info">
          No imaging studies have been requested for this patient.
        </Alert>
      )}
      {requests.map((request) => (
        <RequestCard
          key={request._id}
          request={request}
          epcrID={epcrID}
          viewerIsApprover={viewerIsApprover}
          onReview={setReviewing}
          onRecordImpression={setRecordingImpression}
          onUpdated={replaceRequest}
          onDeleted={removeRequest}
        />
      ))}
      <NewRequestDialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        epcrID={epcrID}
        modalities={imagingSettings?.modalities ?? []}
        onCreated={(created) => {
          setRequests((prev) => [created, ...prev])
          onRequestCreated?.(created)
          setOpenNew(false)
        }}
      />
      <ImagingReviewDrawer
        request={reviewing}
        showRecordLink={false}
        onClose={() => setReviewing(null)}
        onDecided={(updated) => {
          replaceRequest(updated)
          setReviewing(null)
        }}
      />
      <ImpressionDialog
        // Remount per request so field state doesn't leak between studies
        key={recordingImpression?._id ?? 'impression'}
        request={recordingImpression}
        epcrID={epcrID}
        onImagesChanged={replaceRequest}
        onClose={() => setRecordingImpression(null)}
        onUpdated={(updated) => {
          replaceRequest(updated)
          setRecordingImpression(null)
        }}
      />
    </Stack>
  )

  if (title) return <DetailsBox title={title}>{content}</DetailsBox>
  return content
}

export default ImagingRequestsPanel
